import { useSyncExternalStore } from 'react';
import { configData } from '../../config';
import { getAttributionCookie, clearAttributionCookie } from './AttributionService';

// Quick form -> full application: the Drupal theme's drop-in (SMWW-QUICKFORM-APP3 v1.0.2,
// sites/all/themes/smww/js/smww-quickapp.js) for this site's quick forms.
// A quick form is step 1 of the application the /application page posts (application/step1);
// steps 2 and 3 follow in a popup (QuickAppPopup).

// Course list and groups as the application page shows them
export const COURSES = [
    ['Agent', [['AM400', 'Athlete Management (Sports Agent)'], ['AMB', 'Athlete Marketing & Branding'], ['BAM', 'Baseball Agent'], ['BBAM', 'Basketball Agent'], ['SCPC', 'FIFA Agent Prep'], ['FACPC', 'NFLPA Certification Prep'], ['HAM', 'Hockey Agent'], ['NIL', 'Name, Image, and Likeness'], ['CAPOLOGY', 'Salary Cap & Contract Negotiation'], ['SAM', 'Soccer Agent']]],
    ['Baseball', [['BAC', 'Baseball Analytics'], ['BGM100', 'Baseball GM & Scouting'], ['BPD', 'Baseball Player Development']]],
    ['Basketball', [['BBAC', 'Basketball Analytics'], ['BGMSC', 'Basketball GM & Scouting'], ['BBPD', 'Basketball Player Development'], ['DVS', 'Basketball Video Performance Analysis With Synergy']]],
    ['Cricket', [['CMA100', 'Cricket Business Management']]],
    ['Esports', [['ESPORTS', 'Esports Business']]],
    ['Flag Football', [['FLAG', 'Flag Football Leadership']]],
    ['Football', [['FAC', 'Football Analytics'], ['FGMSC', 'Football GM & Scouting'], ['FPD', 'Football Player Development'], ['DVE', 'Football Video Performance Analysis With Catapult']]],
    ['Hockey', [['HAC', 'Hockey Analytics'], ['HGM100', 'Hockey GM & Scouting'], ['HPD', 'Hockey Player Development'], ['DVEH', 'Hockey Video Performance Analysis With Catapult']]],
    ['Motorsports', [['ARMC', 'Motorsports Management']]],
    ['Rugby', [['RM100', 'Rugby Business Management']]],
    ['Soccer / Football / Fútbol', [['SCPC', 'FIFA Agent Prep'], ['SAC', 'Soccer Analytics'], ['SGM100', 'Soccer Management & Scouting'], ['STI', 'Soccer Player Development']]],
    ['All Sports', [['COACH', 'Coaching At The Next Level'], ['DSA', 'Sports Analytics and AI'], ['HSAD', 'Sports Administration'], ['SB100', 'Sports Broadcasting'], ['SCS', 'Sports Career Strategies'], ['SPORTSPR', 'Sports Media & Content Strategy'], ['SRM400', 'Sports Sales & Marketing']]],
    ['Advanced Certificates', [['ARAC', 'Sports Agent Certificate'], ['BFOAC', 'Baseball Front Office Certificate'], ['BBFOAC', 'Basketball Front Office Certificate'], ['FFOAC', 'Football Front Office Certificate'], ['HFOAC', 'Hockey Front Office Certificate'], ['SFOAC', 'Soccer Front Office Certificate']]],
    ['Apprenticeships & Internships', [['BBAS', 'Basketball Apprenticeship'], ['FBAAS', 'Football Agent Apprenticeship'], ['FBAS', 'Football Scout Apprenticeship'], ['SCAS', 'Soccer Apprenticeship'], ['VIP', 'Virtual Internship Program']]],
];
export const DEGREES = [['MASM', "Master's of Professional Studies in Sports Management (FHSU)"], ['MASL', "Master's in Sports Leadership (CUC)"], ['MBA', 'MBA in Sports Leadership (CUC)'], ['Ed.D', 'Ed.D. Sports Leadership (CUC)'], ['Ph.D', 'Ph.D. Sports Leadership (CUC)']];
export const GRADES = ['High School Diploma', 'Some College', "Bachelor's Degree", "Master's Degree", 'Doctoral Degree'];
export const HEARD = ['College Professor', 'Dr. Lashbrook gave me his card', 'Facebook', 'Former Customer/Conference Attendee', 'Google', 'Instagram', 'LinkedIn', 'Podcast', 'Radio Ad', 'Received an SMWW newsletter email', 'Referred by an SMWW Alumni', 'Twitter', 'YouTube'];
export const GENDERS = ['Male', 'Female', 'Non-binary', 'Prefer not to answer'];

// Every course code above, in the order listed (FIFA Agent Prep is listed twice)
const COURSE_CODES = [...new Set(COURSES.flatMap(([, courses]) => courses.map(([code]) => code)))];
const DEGREE_CODES = DEGREES.map(([code]) => code);

// Error texts of the application page
export const QUICK_APP_MESSAGES = {
    email_blocked: 'Please enter your email address',
    street_address: 'Street Address is required',
    postal_code: 'Postal Code is required',
    postal_invalid: 'Invalid Zipcode',
    field_birthday: 'Birthday is required',
    birthday_invalid: 'Please enter a real date',
    gender: 'Gender is required',
    appliedCourses: 'Please select any one of the course',
    story: 'Tell Us story is required',
    field_highest_grade: 'Education is required',
    field_linkedin_url: 'Please enter a valid LinkedIn URL',
    save: "We couldn't save that. Please check your connection and try again.",
};

// Where the popup's last screen sends the applicant
export const QUICK_APP_DONE_URL = '/smww_pay';

const BLOCKED_EMAILS = ['info@smww.com', 'regan.jim65@gmail.com', 'aweraa@proton.me',
    'jamesfregan68@gmaik.com', 'jamesfregan68@gmail.com', 'lisaostrom90@gmail.com', 'molinok@gmail.com'];
// A state is asked for in these countries, a province everywhere else
const STATE_COUNTRIES = { 'United States': 'US', 'Canada': 'CA' };
const REQUEST_TIMEOUT_MS = 20000;

export const isBlockedEmail = (email) => BLOCKED_EMAILS.includes((email || '').trim().toLowerCase());

// ---------- the application in progress ----------
// One at a time, shared by every quick form on the page (the desktop and mobile copies are
// separate React roots) and by the popup: { id, path, stage, open, state }.
// stage is the step to fill in next (2 or 3), or 4 once the application is complete.
let app = null;
let appCount = 0;
const listeners = new Set();

function setApp(next) {
    app = next;
    listeners.forEach((cb) => cb());
}

export function subscribeQuickApp(cb) {
    listeners.add(cb);
    return () => { listeners.delete(cb); };
}

export const getQuickApp = () => app;

// For a quick form: the application it should show in place of its fields. One started on
// another page leaves this page's form as it is, as on the Drupal site.
export function useQuickApp() {
    const current = useSyncExternalStore(subscribeQuickApp, getQuickApp);
    return current && current.path === window.location.pathname ? current : null;
}

export function openQuickApp() {
    if (app && !app.open) setApp({ ...app, open: true });
}

export function closeQuickApp() {
    if (app && app.open) setApp({ ...app, open: false });
}

// ---------- helpers ----------
function gclid() {
    const fromUrl = new URLSearchParams(window.location.search).get('gclid');
    if (fromUrl) {
        document.cookie = 'gclid=' + encodeURIComponent(fromUrl) + '; max-age=7776000; path=/';
        return fromUrl;
    }
    const parts = ('; ' + document.cookie).split('; gclid=');
    return parts.length === 2 ? decodeURIComponent(parts.pop().split(';').shift()) : null;
}

function attribution() {
    try { return getAttributionCookie(); } catch (e) { return null; }
}

// The events the application page sends, marked as coming from a quick form
function gaEvent(step, state) {
    if (typeof window.gtag !== 'function') return;
    try {
        window.gtag('event', 'main_app_step_' + step, {
            email: state.email,
            first_name: state.first_name,
            last_name: state.last_name,
            form_source: 'quick_form',
        });
    } catch (e) { /* never block the form */ }
}

async function post(path, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
        const response = await fetch(configData.APP_URL + path, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
            signal: controller.signal,
        });
        const text = await response.text();
        let json = null;
        try { json = JSON.parse(text); } catch (e) { /* not JSON */ }
        return { ok: response.ok, json };
    } finally {
        clearTimeout(timer);
    }
}

const created = (res) => !!(res.ok && res.json?.data?.result === 'user_created');

function realDate(year, month, day) {
    const date = new Date(+year, +month - 1, +day);
    return date.getFullYear() === +year && date.getMonth() === +month - 1 && date.getDate() === +day && date <= new Date();
}

function trimmed(fields) {
    const out = {};
    Object.keys(fields).forEach((key) => {
        out[key] = typeof fields[key] === 'string' ? fields[key].trim() : fields[key];
    });
    return out;
}

// ---------- step 1: a quick form ----------
// form: the quick form's fields (fname, lname, email, phone, fteam, fcity, fcountry, fstate,
// fprovince, field_text_me_with_news_offers, sku, page_type); countries: the list its country
// select was filled from. Posted in the shape the application page posts its first step.
// Resolves to true when it was saved; the application is then in progress at step 2.
export async function submitQuickAppStep1(form, countries) {
    const value = (name) => String(form[name] || '').trim();
    // The quick forms hold the country's name; the application takes its ISO code
    const country = Object.values(countries || {}).find((c) => c.label === form.fcountry)?.value
        || STATE_COUNTRIES[form.fcountry] || '';
    const hasStates = Object.values(STATE_COUNTRIES).includes(country);
    const sku = value('sku');
    const state = {
        first_name: value('fname'), last_name: value('lname'), email: value('email'), primary_phone: value('phone'),
        city: value('fcity'), country,
        // "-" is the state list's "Outside the US or Canada"
        state: hasStates && value('fstate') !== '-' ? value('fstate') : '',
        province: hasStates ? '' : value('fprovince'),
        field_favteam: value('fteam'), field_secondary_phone: '', street_address: '', field_street_2: '', postal_code: '',
        birthdayMonth: '', birthdayDay: '', birthdayYear: '', gender: '',
        // The page's course is ticked in step 3 when its sku is a course code (a conference's is its title)
        appliedCourses: COURSE_CODES.includes(sku) ? [sku] : [],
        cuc_degrees: [], story: '', field_current_job: '', educationLevel: '', field_university: '', field_major: '',
        field_last_attended: '', field_military: '', field_military_id: '', field_highest_grade: '',
        field_sports_recom: '', field_linkedin_url: '', firststepuid: '', secondstepuid: '', thirdstepuid: '',
        pageURL: window.location.href.split('#')[0] + '#quick-form',
        showAdditionalFieldsStep2: false, showAdditionalFieldsStep3: false, showWelcomeTextStep2: true,
        showWelcomeTextStep3: true, showStateField: hasStates, showProvinceField: !hasStates, showEducationFields: false,
        newUser: 0, continueone: true, continuetwo: false,
        field_text_me_with_news_offers: Number(form.field_text_me_with_news_offers) ? '1' : '0',
        gclid: gclid(),
        // Where the form was filled in, for the application notes (pageURL carries the address)
        source_page_title: document.title.trim(), source_page_type: value('page_type'), source_page_sku: sku,
    };

    let res;
    try {
        res = await post('application/step1', { ...state, attribution: attribution() });
    } catch (e) {
        return false;
    }
    if (!created(res)) return false;

    clearAttributionCookie();
    state.firststepuid = res.json.data.drupal_uid;
    state.showAdditionalFieldsStep2 = true;
    state.continueone = false;
    state.continuetwo = true;
    if (res.json.data.user_status === 'exist_user') state.showWelcomeTextStep2 = false;
    else state.newUser = 1;
    gaEvent(1, state);
    setApp({ id: ++appCount, path: window.location.pathname, stage: 2, open: false, state });
    return true;
}

// ---------- step 2: about you ----------
// Resolves to the problems found, by field name; none when the step can be saved
export async function checkQuickAppStep2(fields) {
    const s = { ...app.state, ...trimmed(fields) };
    const errors = {};
    if (!s.street_address) errors.street_address = QUICK_APP_MESSAGES.street_address;
    if (!s.postal_code) errors.postal_code = QUICK_APP_MESSAGES.postal_code;
    if (!(s.birthdayMonth && s.birthdayDay && s.birthdayYear)) errors.field_birthday = QUICK_APP_MESSAGES.field_birthday;
    else if (!realDate(s.birthdayYear, s.birthdayMonth, s.birthdayDay)) errors.field_birthday = QUICK_APP_MESSAGES.birthday_invalid;
    if (!s.gender) errors.gender = QUICK_APP_MESSAGES.gender;
    if (s.country === 'US' && s.postal_code) {
        try {
            const res = await post('application/validatezipcode', { country: s.country, state: s.state, zip: s.postal_code });
            if (res.json?.data?.result === 'Not Valid') errors.postal_code = QUICK_APP_MESSAGES.postal_invalid;
        } catch (e) { /* the application page also goes on when the check cannot run */ }
    }
    return errors;
}

// Resolves to true when the step was saved; the application is then at step 3
export async function saveQuickAppStep2(fields) {
    const current = app;
    const state = { ...current.state, ...trimmed(fields) };
    let res;
    try {
        res = await post('application/step2', state);
    } catch (e) {
        return false;
    }
    if (!created(res)) return false;

    state.secondstepuid = res.json.data.drupal_uid;
    state.showAdditionalFieldsStep3 = true;
    state.continuetwo = false;
    state.showWelcomeTextStep3 = res.json.data.user_status === 'exist_user' ? state.newUser === 1 : true;
    gaEvent(2, state);
    setApp({ ...current, stage: 3, state });
    return true;
}

// ---------- step 3: your goals ----------
export function checkQuickAppStep3(fields) {
    const s = trimmed(fields);
    const errors = {};
    if (!s.appliedCourses.length) errors.appliedCourses = QUICK_APP_MESSAGES.appliedCourses;
    if (!s.story) errors.story = QUICK_APP_MESSAGES.story;
    if (!s.field_highest_grade) errors.field_highest_grade = QUICK_APP_MESSAGES.field_highest_grade;
    if (s.field_linkedin_url && !s.field_linkedin_url.toLowerCase().includes('linkedin.com/')) errors.field_linkedin_url = QUICK_APP_MESSAGES.field_linkedin_url;
    return errors;
}

// Resolves to true when the step was saved; the application is then complete
export async function saveQuickAppStep3(fields) {
    const current = app;
    const state = { ...current.state, ...trimmed(fields) };
    // Courses and degrees go in the order they are listed, each once
    state.appliedCourses = COURSE_CODES.filter((code) => fields.appliedCourses.includes(code));
    state.cuc_degrees = DEGREE_CODES.filter((code) => fields.cuc_degrees.includes(code));
    state.educationLevel = state.field_highest_grade;
    state.showEducationFields = !!state.field_highest_grade;
    if (state.field_military !== 'Yes') state.field_military_id = '';
    let res;
    try {
        res = await post('application/step3', state);
    } catch (e) {
        return false;
    }
    // Like the application page: a 2xx answer that is not JSON still counts as saved
    if (created(res)) gaEvent(3, state);
    else if (!(res.ok && !res.json)) return false;

    setApp({ ...current, stage: 4, state });
    return true;
}
